import React, { useState, useEffect } from 'react';
import { firestore } from './common/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Router } from 'lucide-react';
import { parseCoordinate, formatCoordinateInput } from './systemSettingsUtils';

const EMPTY_VALUES = {
	name: '',
	reference: '',
	notes: '',
	location: '',
	latitude: '',
	longitude: '',
	serial: '',
	sacn_universe: '',
};

function gatewayToFormValues(gateway) {
	if (!gateway) return { ...EMPTY_VALUES };
	return {
		name: gateway.name || '',
		reference: gateway.reference || '',
		notes: gateway.notes || '',
		location: gateway.location || '',
		latitude: formatCoordinateInput(gateway.latitude),
		longitude: formatCoordinateInput(gateway.longitude),
		serial: gateway.serial || gateway.device_id || '',
		sacn_universe: gateway.sacn_universe || gateway.config?.sacn?.universe || '',
	};
}

function GatewayEditor({ gateway, open, doneEdit, system }) {
	const [values, setValues] = useState({ ...EMPTY_VALUES });
	const isNew = !gateway?.device_id && !gateway?.uid;

	useEffect(() => {
		if (gateway) setValues(gatewayToFormValues(gateway));
	}, [gateway]);

	const save = async () => {
		const id = gateway?.device_id || gateway?.uid || values.serial || 'gw-' + Date.now();
		const data = {
			name: values.name || '',
			reference: values.reference || '',
			notes: values.notes || '',
			location: values.location || '',
			serial: values.serial || id,
			system_id: system?.uid,
			device_id: id,
		};

		const latitude = parseCoordinate(values.latitude, -90, 90);
		const longitude = parseCoordinate(values.longitude, -180, 180);
		if (latitude != null) data.latitude = latitude;
		if (longitude != null) data.longitude = longitude;

		if (isNew) {
			data.createdAt = serverTimestamp();
		}

		if (values.sacn_universe) {
			data.config = data.config || {};
			data.config.sacn = { universe: parseInt(values.sacn_universe, 10) || 1 };
		}

		await setDoc(doc(firestore, 'gateways', id), data, { merge: true });
		doneEdit();
	};

	if (!open) return null;

	return (
		<div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
			<div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[92vh] overflow-auto">
				<div className="p-4 sm:p-6">
					<h3 className="font-semibold mb-4 flex items-center gap-2">
						<Router size={18} /> {isNew ? 'Add Gateway' : 'Edit Gateway'}
					</h3>

					<div className="space-y-3">
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400">Name</label>
							<input
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full"
								value={values.name}
								onChange={(e) => setValues({ ...values, name: e.target.value })}
								placeholder="Gateway name"
							/>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400">Reference</label>
							<input
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full"
								value={values.reference}
								onChange={(e) => setValues({ ...values, reference: e.target.value })}
								placeholder="Short reference label (e.g. North Tower)"
							/>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400">Serial / Device ID</label>
							<input
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full"
								value={values.serial}
								onChange={(e) => setValues({ ...values, serial: e.target.value })}
								placeholder="Serial ID"
								disabled={!isNew}
							/>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400">City / Location</label>
							<input
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full"
								value={values.location}
								onChange={(e) => setValues({ ...values, location: e.target.value })}
								placeholder="City or venue"
							/>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div>
								<label className="text-xs text-gray-500 dark:text-gray-400">Latitude</label>
								<input
									type="number"
									step="any"
									min="-90"
									max="90"
									className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full text-sm"
									value={values.latitude}
									onChange={(e) => setValues({ ...values, latitude: e.target.value })}
									placeholder="53.5461"
								/>
							</div>
							<div>
								<label className="text-xs text-gray-500 dark:text-gray-400">Longitude</label>
								<input
									type="number"
									step="any"
									min="-180"
									max="180"
									className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full text-sm"
									value={values.longitude}
									onChange={(e) => setValues({ ...values, longitude: e.target.value })}
									placeholder="-113.4938"
								/>
							</div>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400">Notes</label>
							<textarea
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full resize-none"
								rows={2}
								value={values.notes}
								onChange={(e) => setValues({ ...values, notes: e.target.value })}
								placeholder="Installation notes, access details, etc."
							/>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400">sACN Universe (optional)</label>
							<input
								type="number"
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2.5 w-full"
								value={values.sacn_universe}
								onChange={(e) => setValues({ ...values, sacn_universe: e.target.value })}
								placeholder="1"
							/>
						</div>
					</div>
				</div>

				<div className="px-6 py-4 border-t dark:border-gray-700 flex gap-3 justify-end bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
					<button onClick={doneEdit} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</button>
					<button onClick={save} className="px-4 py-2 text-sm bg-[#880e4f] text-white rounded-lg hover:bg-[#6b0b3f]">Save Gateway</button>
				</div>
			</div>
		</div>
	);
}

export default GatewayEditor;
